import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications, NOTIFICATION_TYPES, PRIORITY, formatRelativeTime } from '../../hooks/useNotifications.jsx';
import PushToggle from '../common/PushToggle.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { useTranslation } from 'react-i18next'

// Relative time — delegate to the shared formatRelativeTime, which normalises the backend's
// NAIVE-UTC timestamps (via parseServerDate) before comparing. The old local formatTimeAgo did
// `new Date(naiveString)`, which the browser reads as LOCAL time — so an IST (UTC+5:30) user saw
// a just-now notification as "5h ago". Using the shared helper fixes that for every timezone.
const formatTimeAgo = (timestamp) => formatRelativeTime(timestamp);

// Get icon and color based on notification type
function getNotificationStyle(type, priority) {
    const styles = {
        [NOTIFICATION_TYPES.FUNDING_REQUEST]: {
            icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
            bgColor: 'bg-blue-100',
            iconColor: 'text-blue-600'
        },
        [NOTIFICATION_TYPES.FUNDING_APPROVED]: {
            icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
            bgColor: 'bg-green-100',
            iconColor: 'text-green-600'
        },
        [NOTIFICATION_TYPES.FUNDING_REJECTED]: {
            icon: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
            bgColor: 'bg-red-100',
            iconColor: 'text-red-600'
        },
        [NOTIFICATION_TYPES.BUDGET_WARNING]: {
            icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
            bgColor: 'bg-amber-100',
            iconColor: 'text-amber-600'
        },
        [NOTIFICATION_TYPES.BUDGET_exceed]: {
            icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
            bgColor: 'bg-red-100',
            iconColor: 'text-red-600'
        },
        [NOTIFICATION_TYPES.TASK_ASSIGNED]: {
            icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
            bgColor: 'bg-indigo-100',
            iconColor: 'text-indigo-600'
        },
        [NOTIFICATION_TYPES.TASK_OVERDUE]: {
            icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
            bgColor: 'bg-red-100',
            iconColor: 'text-red-600'
        },
        [NOTIFICATION_TYPES.PROJECT_CREATED]: {
            icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
            bgColor: 'bg-purple-100',
            iconColor: 'text-purple-600'
        },
        [NOTIFICATION_TYPES.PROJECT_STATUS]: {
            icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
            bgColor: 'bg-cyan-100',
            iconColor: 'text-cyan-600'
        },
        [NOTIFICATION_TYPES.RECOMMENDATION]: {
            icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
            bgColor: 'bg-yellow-100',
            iconColor: 'text-yellow-600'
        },
        [NOTIFICATION_TYPES.CONSULTANT_added]: {
            icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
            bgColor: 'bg-teal-100',
            iconColor: 'text-teal-600'
        },
        [NOTIFICATION_TYPES.COMMENT]: {
            icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
            bgColor: 'bg-gray-100',
            iconColor: 'text-gray-600'
        }
    };

    return styles[type] || styles[NOTIFICATION_TYPES.COMMENT];
}

// Priority badge
function PriorityBadge({ priority }) {
    const colors = {
        [PRIORITY.LOW]: 'bg-gray-100 text-gray-600',
        [PRIORITY.MEDIUM]: 'bg-blue-100 text-blue-600',
        [PRIORITY.HIGH]: 'bg-orange-100 text-orange-600',
        [PRIORITY.CRITICAL]: 'bg-red-100 text-red-600'
    };

    if (priority === PRIORITY.LOW) return null;

    return (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium round uppercase ${colors[priority]}`}>
            {priority}
        </span>
    );
}

// Single notification item
function NotificationItem({ notification, onClose }) {
  const { t: tch } = useTranslation('chrome')
    const { markAsRead } = useNotifications();
    const style = getNotificationStyle(notification.type, notification.priority);

    const handleClick = () => {
        markAsRead(notification.id);
        onClose();
    };

    return (
        <Link
            to={notification.link || '#'}
            onClick={handleClick}
            className={`flex items-start gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${!notification.read ? 'bg-blue-50/50' : ''
                }`}
        >
            {/* Icon */}
            <div className={`flex-shrink-0 w-9 h-9 rounded-full ${style.bgColor} flex items-center justify-center`}>
                <svg className={`w-4 h-4 ${style.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={style.icon} />
                </svg>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium text-gray-900 truncate ${!notification.read ? 'font-semibold' : ''}`}>
                        {notification.title}
                    </p>
                    <PriorityBadge priority={notification.priority} />
                </div>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
                <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-gray-400">{formatTimeAgo(notification.timestamp)}</span>
                    {notification.actionRequired && (
                        <span className="text-[10px] text-blue-600 font-medium">{tch('action_required')}</span>
                    )}
                </div>
            </div>

            {/* Unread indicator */}
            {!notification.read && (
                <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            )}
        </Link>
    );
}

// Main NotificationBell component
export default function NotificationBell() {
  const { t: tch } = useTranslation('chrome')
    const { notifications: all, isOpen, toggleDropdown, closeDropdown, markAllAsRead } = useNotifications();
    const { user } = useAuth();
    // A Partner exists only inside a connected product, so ProTrack's own contract, milestone and
    // project notices are not theirs to read. Only what points into a product is shown, and the
    // badge counts only that — never the hidden ones.
    const notifications = user?.role === 'Partner'
        ? all.filter(n => (n.link || '').startsWith('/products'))
        : all;
    const unreadCount = notifications.filter(n => !n.read).length;
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                closeDropdown();
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, closeDropdown]);

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={toggleDropdown}
                className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341A6.002 6.002 0 006 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>

                {/* Badge */}
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full ring-2 ring-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown - Responsive: fixed on mobile, absolute on desktop */}
            {isOpen && (
                <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-auto sm:mt-2 w-auto sm:w-96 max-w-[calc(100vw-16px)] sm:max-w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                    {/* header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900">{tch('notifications')}</h3>
                            {unreadCount > 0 && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                    {unreadCount} new
                                </span>
                            )}
                        </div>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>

                    {/* Enable/disable native OS push notifications for this device */}
                    <PushToggle />

                    {/* Notifications List */}
                    <div className="max-h-[400px] overflow-y-auto">
                        {notifications.length > 0 ? (
                            notifications.map(notification => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    onClose={closeDropdown}
                                />
                            ))
                        ) : (
                            <div className="py-12 text-center">
                                <svg className="w-12 h-12 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341A6.002 6.002 0 006 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                <p className="mt-2 text-sm text-gray-500">{tch('no_notifications_yet')}</p>
                                <p className="text-xs text-gray-400">{tch('will_notify')}</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="border-t border-gray-200 bg-gray-50">
                            <Link
                                to="/notifications"
                                onClick={closeDropdown}
                                className="block w-full px-4 py-3 text-center text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-gray-100 transition-colors"
                            >
                                View all notifications
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
